import React, { useCallback, useEffect, useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface BeforeAfterSliderProps {
  beforeImage?: string;
  afterImage?: string;
}
export function BeforeAfterSlider({
  beforeImage = 'https://cdn.magicpatterns.com/uploads/wgBx1JtZ981B9Ro9fFwPum/PFR-Before.jpg',
  afterImage = 'https://cdn.magicpatterns.com/uploads/pwX2GBx2yjQEPjzAz3SZby/PFR-After.jpg'
}: BeforeAfterSliderProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, x / rect.width * 100));
    setSliderPosition(percentage);
  }, []);
  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);
  const onTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  }, [isDragging, handleMove]);
  const stopDragging = useCallback(() => {
    setIsDragging(false);
  }, []);
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', stopDragging);
      window.addEventListener('touchmove', onTouchMove, {
        passive: false
      });
      window.addEventListener('touchend', stopDragging);
    } else {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stopDragging);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stopDragging);
    };
  }, [isDragging, onMouseMove, onTouchMove, stopDragging]);
  const handlePointerDown = (clientX: number) => {
    setIsDragging(true);
    handleMove(clientX);
  };
  return <div ref={containerRef} className={`relative overflow-hidden rounded-[6px] aspect-[16/9] border border-stone-border ${isDragging ? 'cursor-ew-resize select-none' : 'cursor-pointer'}`} onMouseDown={e => handlePointerDown(e.clientX)} onTouchStart={e => handlePointerDown(e.touches[0].clientX)} data-id="element-13" data-mp="8tpmkiqqvvwa">
      {/* After Image (Bottom Layer) */}
      <img src={afterImage} alt="After state" className="absolute inset-0 w-full h-full object-cover pointer-events-none" data-id="element-14" data-mp="wis29l1tjeo5" />

      {/* Before Image (Top Layer) */}
      <img src={beforeImage} alt="Before state" className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={{
      clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
    }} data-id="element-15" data-mp="16gy4y4vg8ou2" />

      {/* Floating Labels */}
      <div className="absolute top-4 left-4 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-ink pointer-events-none shadow-sm" data-id="element-16" data-mp="1l4y2rr0dnhk6">
        Before
      </div>
      <div className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-ink pointer-events-none shadow-sm" data-id="element-17" data-mp="1b5gz1f4ordqu">
        After
      </div>

      {/* Divider Line */}
      <div className="absolute top-0 bottom-0 w-[2px] bg-white pointer-events-none" style={{
      left: `${sliderPosition}%`,
      transform: 'translateX(-50%)'
    }} data-id="element-18" data-mp="11ew5i63vczes" />

      {/* Draggable Handle */}
      <div className="absolute top-1/2 w-10 h-10 bg-white rounded-full border border-stone-border shadow-sm hover:shadow-md transition-shadow flex items-center justify-center cursor-ew-resize z-10" style={{
      left: `${sliderPosition}%`,
      transform: 'translate(-50%, -50%)'
    }} onMouseDown={e => {
      e.stopPropagation();
      setIsDragging(true);
    }} onTouchStart={e => {
      e.stopPropagation();
      setIsDragging(true);
    }} data-id="element-19" data-mp="shbyegjbs8w9">
        <div className="flex items-center pointer-events-none" data-id="element-20" data-mp="1lrz0d6685ni4">
          <ChevronLeftIcon className="w-4 h-4 text-ink" data-id="element-21" data-mp="1luux4r6c664z" />
          <ChevronRightIcon className="w-4 h-4 text-ink" data-id="element-22" data-mp="1h0qrqccpy660" />
        </div>
      </div>
    </div>;
}